import { Credentials } from '../domain/credentials/Credentials';
import { User } from '../domain/user/User';
import { sessionRepository } from '../repositories/session';
import { SessionRepository } from '../repositories/session/SessionRepository';
import { userRepository } from '../repositories/user';
import { UserLocationCountryProps, UserRepository } from '../repositories/user/UserRepository';
import eventsPanel from '../services/events';
import { INTERCOM_EVENTS, intercom } from '../services/intercom';
import { SentryService, sentryService } from './SentryService';
import { webSocketsService } from './WebSocketsService';

export class UserService {
  constructor(
    private userRepository: UserRepository,
    private sessionRepository: SessionRepository,
    private sentryService: SentryService
  ) {}

  getUserLocationByCountry(): UserLocationCountryProps | undefined {
    return this.userRepository.getUserLocationByCountry();
  }

  async validate({ token }: { token: string }): Promise<string> {
    const activeSessionToken = await this.userRepository.validate({ token });

    intercom.track(INTERCOM_EVENTS.user.on_sign_up);

    this.sessionRepository.setToken({ token: activeSessionToken });

    return activeSessionToken;
  }

  validatePhone({ otp_code }: { otp_code: string }): Promise<void> {
    return this.userRepository.validatePhone({ otp_code });
  }

  newPassword({ token, password }: { token: string; password: string }): Promise<void> {
    return this.userRepository.newPassword({ token, password });
  }

  fetchResetPassword({ email }: { email: string }): Promise<void> {
    return this.userRepository.fetchResetPassword({ email });
  }

  resendValidationEmail({ email }: { email: string }): Promise<void> {
    return this.userRepository.resendValidationEmail({ email });
  }

  resendValidationPhone(): Promise<string> {
    return this.userRepository.resendValidationPhone();
  }

  validateNewPasswordToken({ token }: { token: string }): Promise<void> {
    return this.userRepository.validateNewPasswordToken({ token });
  }

  async fetch(): Promise<User> {
    const user = await this.userRepository.fetch();

    if (!this.sessionRepository.getAsUser()) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'setDataLayerData' does not exist o... Remove this comment to see the full error message
      window.setDataLayerData({
        email: user.email,
        phone: user.phone,
      });

      intercom.boot();
      intercom.setUser({
        ...(user.first_name && user.last_name ? { name: `${user.first_name} ${user.last_name}` } : {}),
        firstname: user.first_name,
        lastname: user.last_name,
        user_id: user.id,
        organization_id: user.organization_id,
        default_project_id: user.default_project_id,
        user_language: user.language.value,
        email: user.email,
        phone: user.phone,
        validated: user.email_validated,
        signed_up_at: user.created_at,
        user_hash: user.intercom_hash,
      });
      eventsPanel.identify(user);

      // Setting the user data in Sentry
      this.sentryService.setUser({
        id: user.id,
        email: user.email,
        ...(user.first_name || user.last_name
          ? {
              name: `${user.first_name ? `${user.first_name} ` : ``}${user.last_name ?? ``}`,
            }
          : {}),
        Language: user.language.value,
        Phone: user.phone,
        Validated: user.email_validated,
        SignedUpAt: user.created_at,
      });
    }

    webSocketsService.connect(this.sessionRepository.getToken()!);

    return user;
  }

  async fetchAdmin(): Promise<User> {
    const user = await this.userRepository.fetchAdmin();
    return user;
  }

  async retrieveCredentials(): Promise<Credentials> {
    const credentials = await this.userRepository.retrieveCredentials();
    return credentials;
  }

  async save({ user }: { user: User }): Promise<User> {
    const updatedUser = await this.userRepository.save(user);
    return updatedUser;
  }

  fetchAvatar({ user }: { user: User }): Promise<User> {
    // FEM AQUI LO DE TRACTAR LA IMATGE???
    return this.userRepository.fetchAvatar({ user });
  }

  async createAvatar({
    user,
    fileName,
    mimeType,
    file,
  }: {
    user: User;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<User> {
    const updatedUser = await this.userRepository.createAvatar({ user, fileName, mimeType, file });
    return updatedUser;
  }

  async deleteAvatar({ user }: { user: User }): Promise<User> {
    const updatedUser = await this.userRepository.deleteAvatar({ user });
    return updatedUser;
  }
}

export const userService = new UserService(userRepository, sessionRepository, sentryService);
